
import { defineComponent, reactive, onMounted } from 'vue';
import { Badge } from 'ant-design-vue';
import {
  PlusOutlined,
  ReloadOutlined,
  ColumnHeightOutlined,
  SettingOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
} from '@ant-design/icons-vue';
import { Container as DragContainer, Draggable } from '@/components/draggable';
import {
  queryTag,
  addTag,
  updateTag,
  removeTag,
  recoverTag,
} from '@/api/docker_manager/docker-tag';
import { Pagination, TableFilters, TableColumn, TableColumnSortOrder } from '@/typing';
import { useFetchData } from '@/utils/hooks/useFetchData';
import { useFullscreen } from '@/utils/hooks/useFullscreen';
import { useTableDynamicColumns } from '@/utils/hooks/useTableColumn';
import DragIcon from '@/components/table/drag-icon.vue';
import FormModal from '../../../components/form-modal/form-modal.vue';
import { useStore } from 'vuex';
import { queryOrgList } from '@/api/sys';
import { message } from 'ant-design-vue';
import { useForm } from 'ant-design-vue/es/form';
import SearchRender from '@/components/FormRender/SearchRender.vue';

const statusMap = {
  true: { text: '正常', status: 'green' },
  false: { text: '作废', status: 'red' },
};

const baseColumns: TableColumn[] = [
  {
    title: '序号',
    dataIndex: 'index',
    width: 50,
    slots: { customRender: 'index' },
  },
  {
    title: '名称',
    width: 400,
    dataIndex: 'name',
  },
  {
    title: '状态',
    width: 70,
    dataIndex: 'is_active',
    slots: { customRender: 'status' },
    filters: [
      {
        text: status[0],
        value: true,
      },
      {
        text: status[1],
        value: false,
      },
    ],
  },
  {
    title: '操作',
    width: 200,
    dataIndex: 'action',
    slots: { customRender: 'action' },
  },
];

export default defineComponent({
  name: 'DockerTag',
  setup() {
    const store = useStore();
    const current_org = store.getters['user/current_org'];
    const tableHeight = reactive({ y: window.innerHeight - 130 });
    onMounted(() => {
      window.onresize = function () {
        tableHeight.y = window.innerHeight - 130;
      };
    });
    const {
      state: columnState,
      dynamicColumns,
      dynamicColumnItems,
      handleColumnAllClick,
      handleColumnChange,
      reset,
      move,
    } = useTableDynamicColumns(baseColumns, true);
    const [elRef, screenState, { setFull, exitFull }] = useFullscreen();
    const org_list: any = reactive([]);
    queryOrgList().then(res => {
      res.data.forEach(org => {
        org_list.push(org);
      });
      console.log(org_list);
    });
    const modelRef = reactive({
      org_id: current_org.id,
      is_active: true,
      name: '',
    });
    const config_options = {
      is_active: [
        { name: '全部', value: '' },
        { name: '正常', value: true },
        { name: '作废', value: false },
      ],
    };
    const config = {
      settings: {
        name: '表单名称',
        labelAlign: 'right',
        layout: 'inline',
      },
      fields: [
        {
          type: 'input',
          name: 'name',
          label: '名称',
          disabled: false,
          allowClear: true,
          inputType: 'text',
          maxlength: 100,
          defaultValue: '',
          placeholder: '请填写标签名称',
        },
        {
          type: 'select',
          name: 'is_active',
          label: '状态',
          mode: 'default',
          disabled: false,
          defaultValue: '',
          labelKey: 'name',
          valueKey: 'value',
          placeholder: '请选择状态',
          datasource: 'is_active',
        },
      ],
      rules: {},
      model: modelRef,
    };
    const { validateInfos } = useForm(modelRef, {});

    const {
      stripe,
      reload,
      setPageInfo,
      context: state,
    } = useFetchData(queryTag, {
      current: 1,
      pageSize: 20,
      tableSize: 'middle', // 'default' | 'middle' | 'small'
      stripe: true,
      requestParams: {
        ...modelRef,
      },
    });
    const handleTableChange = (
      { current, pageSize }: Pagination,
      filters: TableFilters,
      sorter: TableColumnSortOrder,
    ) => {
      console.log(sorter);
      setPageInfo({
        current,
        pageSize,
        ...filters,
        ...modelRef,
      });

      reload();
    };

    const search = () => {
      setPageInfo({
        current: 1,
        ...modelRef,
      });
      reload();
    };
    // edit
    const edit_model_config = {
      settings: {
        name: '表单名称',
        labelAlign: 'right',
        layout: 'vertical',
      },
      fields: [
        {
          type: 'input',
          name: 'name',
          label: '名称',
          disabled: false,
          allowClear: true,
          inputType: 'number',
          maxlength: 100,
          defaultValue: '',
          placeholder: '请填标签名称',
        },
      ],
      rules: {
        name: [{ required: true, message: '请输入名称' }],
      },
      model: {
        id: null,
        name: '',
      },
    };
    const edit_model_config_options = {};
    const editModal = reactive({
      visible: false,
      title: '',
      form: edit_model_config,
      options: edit_model_config_options,
    });
    let currentRecord = reactive({ name: '' });
    const handleOpenEdit = (record: any) => {
      editModal.visible = true;
      editModal.title = '修改标签';
      editModal.form.model = {
        ...record,
        name: record.name,
      };
      currentRecord = record;
    };

    const handleDelete = (record: any) => {
      removeTag({ org_id: current_org.id, tag_id: record.id }).then(res => {
        message.success('删除标签成功');
        Object.assign(record, res);
      });
    };
    const handleRecover = (record: any) => {
      recoverTag({ org_id: current_org.id, tag_id: record.id }).then(res => {
        message.success('恢复标签成功');
        Object.assign(record, res);
      });
    };

    const handlerOk = (data: any) => {
      if (!editModal.form.model.id) {
        addTag({ org_id: current_org.id, ...data }).then(() => {
          message.success('新增标签成功');
          reload();
        });
      } else {
        updateTag({
          ...data,
          org_id: current_org.id,
          tag_id: editModal.form.model.id,
        }).then((res: any) => {
          Object.assign(currentRecord, res);
          message.success('修改标签成功');
        });
      }
      editModal.visible = false;
      // reload();
    };

    return {
      statusMap,
      tableHeight,
      state,
      org_list,
      stripe,
      columnState,
      dynamicColumns,
      dynamicColumnItems,

      handlerOk,
      currentRecord,
      modelRef,
      validateInfos,
      // fullscreen
      elRef,
      screenState,
      setFull,
      exitFull,

      handleTableChange,
      search,

      handleColumnChange,
      handleColumnAllClick,
      reset,
      move,
      current_org,
      message,
      addTag,
      updateTag,
      removeTag,
      recoverTag,
      // edit
      edit_model_config,
      edit_model_config_options,
      editModal,
      handleOpenEdit,
      handleDelete,
      handleRecover,
      reload,
      config,
      config_options,
    };
  },
  components: {
    DragIcon,
    PlusOutlined,
    ReloadOutlined,
    ColumnHeightOutlined,
    SettingOutlined,
    FullscreenOutlined,
    FullscreenExitOutlined,
    Draggable,
    DragContainer,
    [Badge.name]: Badge,
    FormModal,
    SearchRender,
  },
});
